/**
 * WriterStatsCard.jsx — Phase-1 Writer System stats panel.
 *
 * Renders on the Scribe Dashboard. Shows evolution tier + level, XP progress
 * bar, coin balance with £ equivalent, and the 5 core stats (Submission Rate,
 * Approval Rate, Audience Reach, Content Depth, Vault Prestige).
 *
 * The assessment banner shows only when the scribe has NOT completed the
 * assessment yet — Phase 1 is intentionally soft-gate (banner only); hard
 * enforcement ships in Phase 2 along with the 3-path chooser UI.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, Coins, TrendingUp, Users, Layers, Lock as LockIcon,
  CheckCircle2, AlertCircle, FileText,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Match the spec's evolution tier palette so badge colour = tier colour.
const TIER_GLOW = {
  'Ink Novice':              'from-slate-400/25 via-slate-500/10',
  'Wordsmith Apprentice':    'from-amber-700/25 via-amber-800/10',
  'Story Practitioner':      'from-orange-600/25 via-orange-700/10',
  'Narrative Adept':         'from-yellow-400/25 via-yellow-500/10',
  'Craft Expert':            'from-emerald-500/25 via-emerald-600/10',
  'Master Scribe':           'from-blue-500/25 via-blue-600/10',
  'Sage Author':             'from-red-500/25 via-red-600/10',
  'Luminary Storyteller':    'from-purple-500/25 via-purple-600/10',
  'Grandmaster Visionary':   'from-cyan-400/25 via-cyan-500/10',
  'Legendary Archon':        'from-gray-200/25 via-gray-300/10',
};

const StatTile = ({ icon: Icon, label, value, sub, colour = 'text-white', testId }) => (
  <div className="p-3 bg-slate-900/40 rounded-lg border border-slate-700/50" data-testid={testId}>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-400 mb-1">
      <Icon className="w-3.5 h-3.5" />
      {label}
    </div>
    <div className={`text-xl font-bold ${colour}`}>{value}</div>
    {sub && <div className="text-[11px] text-gray-400 mt-0.5">{sub}</div>}
  </div>
);

const WriterStatsCard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const res = await authFetch(`${BACKEND_URL}/api/writer/stats`);
      if (!res.ok) throw new Error(`stats ${res.status}`);
      setStats(await res.json());
      setError('');
    } catch (e) {
      setError(e.message || 'Failed to load writer stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const completeAssessment = () => {
    // Kicks off the guided assessment flow; completion flips
    // assessmentCompleted server-side and initialises writingPaths.
    navigate('/writer-assessment');
  };

  if (loading) {
    return (
      <Card className="bg-slate-800/50 border-slate-700" data-testid="writer-stats-card">
        <CardContent className="p-6 text-gray-400 text-sm">Loading Scribe stats…</CardContent>
      </Card>
    );
  }
  if (error || !stats) {
    return (
      <Card className="bg-slate-800/50 border-slate-700" data-testid="writer-stats-card">
        <CardContent className="p-6 text-rose-300 text-sm">Couldn&apos;t load Scribe stats — {error}</CardContent>
      </Card>
    );
  }

  const tier = stats.tierMeta || { title: stats.writerEvolutionTier, hex: '#9CA3AF' };
  const glow = TIER_GLOW[tier.title] || TIER_GLOW['Ink Novice'];

  const xpInLevel = stats.writerXPInCurrentLevel || 0;
  const xpThreshold = (stats.writerLevel >= 100)
    ? 25000
    : [1000, 2000, 3500, 5000, 7000, 9000, 12000, 15000, 20000, 25000][
        Math.floor((Math.max(1, stats.writerLevel) - 1) / 10)
      ];
  const pct = Math.min(100, Math.round((xpInLevel / xpThreshold) * 100));
  const coins = stats.coinBalance || 0;
  const gbp = stats.gbpBalance ?? (coins / 100);

  const sub = stats.submissionStats || {};
  const aud = stats.audienceStats || {};
  const depth = stats.contentDepth || {};
  const byType = sub.byType || {};

  return (
    <Card className="bg-slate-800/50 border-slate-700 overflow-hidden" data-testid="writer-stats-card">
      {/* Tier-coloured glow header */}
      <div className={`relative p-5 bg-gradient-to-br ${glow} to-transparent border-b border-slate-700/50`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider"
                style={{ backgroundColor: `${tier.hex}22`, color: tier.hex, border: `1px solid ${tier.hex}55` }}
                data-testid="writer-tier-badge"
              >
                <Sparkles className="w-3 h-3" /> {tier.title}
              </span>
              <span className="text-[11px] text-gray-400">
                Levels {tier.min}–{tier.max}
              </span>
            </div>
            <h3 className="text-2xl font-bold text-white">
              Level {stats.writerLevel}
              <span className="text-sm text-gray-400 ml-2 font-normal">
                {stats.writerTotalXP.toLocaleString()} lifetime XP
              </span>
            </h3>
          </div>

          {/* Coin balance — clickable, navigates to Profile → Earnings & Payout. */}
          <button
            type="button"
            onClick={() => navigate('/profile#earnings')}
            className="text-right group rounded-lg px-2 py-1 -mx-2 hover:bg-white/5 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-400/60"
            data-testid="writer-coin-balance"
            title="Open Earnings & Payout"
          >
            <div className="flex items-center justify-end gap-1.5 text-amber-300">
              <Coins className="w-5 h-5" />
              <span className="text-2xl font-extrabold group-hover:text-amber-200">{coins.toLocaleString()}</span>
            </div>
            <div className="text-[11px] text-gray-400">
              £{gbp.toFixed(2)} available
              {stats.coinBalanceLocked > 0 && (
                <span className="ml-1 text-amber-400/80">
                  · {stats.coinBalanceLocked.toLocaleString()} locked
                </span>
              )}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-amber-400/70 group-hover:text-amber-300 mt-0.5">
              Request payout →
            </div>
          </button>
        </div>

        {/* XP progress bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-[11px] text-gray-400 mb-1">
            <span>XP to next level</span>
            <span>{xpInLevel.toLocaleString()} / {xpThreshold.toLocaleString()}</span>
          </div>
          <div className="h-2 bg-slate-900/60 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${pct}%`, backgroundColor: tier.hex }}
              data-testid="writer-xp-bar"
            />
          </div>
        </div>
      </div>

      {/* Assessment banner (Phase-1 soft gate) */}
      {!stats.assessmentCompleted && (
        <div
          className="px-5 py-3 bg-amber-500/10 border-b border-amber-500/30 flex items-center gap-3 flex-wrap"
          data-testid="writer-assessment-banner"
        >
          <AlertCircle className="w-4 h-4 text-amber-300 shrink-0" />
          <p className="text-sm text-amber-100 flex-1 min-w-0">
            Complete the Scribe Assessment to unlock your writing paths and earnings tab.
            <span className="block text-[11px] text-amber-300/80">Takes 2–3 minutes.</span>
          </p>
          <Button
            size="sm"
            onClick={completeAssessment}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold h-8 text-xs"
            data-testid="writer-assessment-start-btn"
          >
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
            Start Assessment
          </Button>
        </div>
      )}

      <CardHeader className="pb-2">
        <CardTitle className="text-white text-base flex items-center gap-2">
          <FileText className="w-4 h-4 text-indigo-400" /> Core Stats
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="writer-core-stats">
        <StatTile
          icon={FileText}
          label="Submission Rate"
          value={sub.totalSubmissions || 0}
          sub={`${sub.uploadsThisMonth || 0} this month`}
          testId="writer-stat-submission"
        />
        <StatTile
          icon={CheckCircle2}
          label="Approval Rate"
          value={`${(sub.approvalRate || 0).toFixed(1)}%`}
          sub={`${sub.approvedTotal || 0} / ${sub.totalSubmissions || 0} approved`}
          colour="text-emerald-300"
          testId="writer-stat-approval"
        />
        <StatTile
          icon={Users}
          label="Audience Reach"
          value={(aud.totalFollowers || 0).toLocaleString()}
          sub={`+${aud.followersThisMonth || 0} this month · next milestone ${aud.followerMilestoneNext || 100}`}
          colour="text-sky-300"
          testId="writer-stat-audience"
        />
        <StatTile
          icon={Layers}
          label="Content Depth"
          value={`${depth.depthScore || 0}/100`}
          sub={`${depth.seriesEpisodeCount || 0} series eps · ${depth.singleCount || 0} singles`}
          colour="text-purple-300"
          testId="writer-stat-depth"
        />
        <StatTile
          icon={LockIcon}
          label="Vault Prestige"
          value={`${stats.vaultPrestige || 0}/100`}
          sub={`${byType.vault || 0} submitted · ${byType.vaultApproved || 0} approved`}
          colour="text-fuchsia-300"
          testId="writer-stat-vault"
        />
      </CardContent>

      {/* Recent activity log (last 5) */}
      {stats.activityLog && stats.activityLog.length > 0 && (
        <div className="px-5 pb-5" data-testid="writer-activity-log">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-400 mb-2">
            <TrendingUp className="w-3.5 h-3.5" />
            Recent activity
          </div>
          <ul className="space-y-1">
            {stats.activityLog.slice(0, 5).map((e, i) => (
              <li
                key={`${e.eventKey}-${i}`}
                className="flex items-center justify-between gap-3 text-xs p-2 bg-slate-900/30 rounded-md border border-slate-800"
              >
                <span className="text-gray-200 truncate">{e.description || e.eventKey}</span>
                <span className="shrink-0 text-amber-300 font-semibold">
                  +{e.xpEarned} XP · +{e.coinsEarned}c
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
};

export default WriterStatsCard;
